import type { ClientGender } from "./gender";
import type { ProfileResearchState } from "./profile-research";

export type Locale = "cs" | "en";

/**
 * F143: which journey the client walks. `full` is everything the product has
 * ever done; `sales_only` is a client whose Buddy was finished OUTSIDE the
 * system, so chapter 1 and the handover never existed for them.
 */
export type JourneyMode = "full" | "sales_only";
export const JOURNEY_MODES: readonly JourneyMode[] = ["full", "sales_only"];
export function isJourneyMode(value: unknown): value is JourneyMode {
  return typeof value === "string" && (JOURNEY_MODES as readonly string[]).includes(value);
}
/**
 * Normalize a raw stored value. Anything unrecognised reads `full`, which is
 * the column's own DEFAULT and the only safe direction: a full client wrongly
 * read as `sales_only` would LOSE the eight steps of chapter 1 they still have
 * to walk, while the reverse merely shows a sales_only client steps their
 * sections already report as untouched.
 */
export function journeyModeOf(value: unknown): JourneyMode {
  return isJourneyMode(value) ? value : "full";
}

export type ApiErrorCode =
  | "invalid_request"
  | "transcription_unavailable"
  | "transcription_failed"
  // The audio arrived and was transcribed; it simply carried no speech. Its
  // own code so the client offers a fresh recording instead of a paid resend
  // of the same silence (F98).
  | "no_speech"
  | "invalid_link"
  | "auth_link_unavailable"
  // F149: the sign-in link died because a NEWER one replaced it - issuing a
  // login link revokes every unredeemed one of that account. Its own code,
  // separate from `auth_link_unavailable`, because the reader's move is
  // different and the generic sentence sent them the wrong way: a working link
  // is already sitting in their inbox, so "request a new one" asks them to
  // kill the very link that would have let them in.
  | "auth_link_superseded"
  | "invite_unavailable"
  | "unauthorized"
  | "forbidden"
  | "not_found"
  | "csrf_invalid"
  | "origin_invalid"
  | "capacity_reached"
  | "capacity_full"
  | "call_cancelled"
  | "idempotency_mismatch"
  | "rate_limited"
  // The take is larger than the transcription route accepts. A repeat of the
  // same bytes can never be accepted, so the sentence must not invite one.
  | "recording_too_large"
  | "state_conflict"
  // F159: a sales-edit result came back in a different LANGUAGE than the
  // material it edits (component markup rewritten as markdown/plain text).
  // The reader cannot render it, so the door refuses it instead of storing a
  // document nobody can read (Tomas Pohl / Litvinova, 2026-08-31).
  | "doc_format_lost"
  | "content_too_large"
  | "progress_conflict"
  | "video_gate_required"
  // F147: the questionnaire refuses for TWO different reasons and the client
  // must be told which. `questionnaire_locked` = the step itself is not open
  // yet (the step before it is unfinished); `profile_confirmation_required` =
  // the step IS open and one named act stands in front of it. Before this both
  // rode a bare `forbidden`/an unnamed 409, so the surface guessed from the
  // STATUS - and a security refusal rendered the locked page's sentence.
  | "questionnaire_locked"
  | "profile_confirmation_required"
  | "q1_training_started"
  | "q2_deliverable_exists"
  | "q2_build_running"
  | "salespage_approval_required"
  // F118 handover: the client's dashboard confirmation asserts that
  // /connectWeb answered, which needs the instance OWNER role. Its own code,
  // separate from state_conflict, because the move is OURS - the sentence the
  // client reads must not send them back to a step they already finished.
  | "owner_role_pending"
  // F134b: the podnět form after the instance changed hands. Its own code,
  // separate from state_conflict, because it is not a step that moved and no
  // reload will bring it back: `/setProOwner` has run, our pool account no
  // longer holds the owner commands, and a request to change the instance is
  // one nobody here can act on.
  | "ownership_transferred"
  /** The team confirmed an owner role for a client who has stored no ID yet. */
  | "handover_identity_missing"
  | "dossier_not_ready"
  /**
   * F155: the console asked which licence a client BOUGHT and we hold no
   * `INTERNAL_DATA_API_SECRET`, so nobody asked the platform. Its own code,
   * separate from a 500 and from an empty list, because all three read alike on
   * screen and only one of them is a claim about the client: an unconfigured
   * lookup must never render as "this client has no licence".
   */
  | "license_lookup_unconfigured"
  /**
   * F152-W4 (audit D16): machine research tried to open a new dossier version
   * over a CURATED one (a map a human brought - the sales_only import manifest,
   * the client's own reset, their applied recordings). Refused, never merged
   * and never overwritten.
   */
  | "dossier_curated"
  | "import_batch_mismatch"
  | "import_incomplete"
  | "invalid_file_type"
  | "active_content_rejected"
  | "file_too_large"
  | "image_dimensions_invalid"
  | "quota_exceeded"
  | "storage_not_ready"
  | "storage_unavailable"
  | "answer_changed"
  | "submission_frozen"
  | "required_answers_missing"
  // F104 staff override of client state: a LOCKED state names itself (bot
  // transfer, publication, stripe, handover close, app release), a state with
  // no reopen semantics answers unavailable, and a missing written reason is
  // its own refusal - never a bare invalid_request.
  | "override_locked"
  | "override_unavailable"
  | "reason_required"
  // F143-W3: the sales_only import lane. `import_mode_unavailable` is the
  // deny-by-default answer of BOTH import doors - the silent staff approve and
  // the q1 submission import - whenever the client is not `sales_only` or the
  // kind is not one the import may close. `submission_import_unsupported` names
  // q2 apart: its submit OPENS the sales pipeline (S12/S13), so importing that
  // row silently would leave a client whose materials nobody builds.
  // `voice_answers_missing` says the import carried no voice, which is the only
  // reason that route exists.
  | "import_mode_unavailable"
  | "submission_import_unsupported"
  | "voice_answers_missing"
  // F154b: the import told the q2 submit to go without a REQUIRED question (the
  // photo nobody has), and named one that is not a required question of this
  // client's catalog. Its own name because it is not a retry and not a missing
  // answer: the caller and the server disagree about which questionnaire this
  // client answers, so the manifest is what to look at.
  | "deferred_not_required"
  // F154: the S25 backfill of a client with NO Buddy claimed it about a client
  // who has one. Its own name because the next move is the opposite of a retry:
  // drop `noBuddy` from the manifest and give the trial link, or check whether
  // the run is pointed at the wrong client.
  | "buddy_present"
  // F106 rebuild from scratch. Every refusal of the regenerate action names
  // itself, because each one asks the partner for a DIFFERENT next move: wait
  // for the running build, finish the running edit, wait for the rebuild
  // already under way, approve the material this one is built on, or accept
  // that there is nothing to rebuild yet.
  | "nothing_to_regenerate"
  | "build_running"
  | "edit_in_progress"
  | "regeneration_in_progress"
  | "prerequisite_missing"
  | "impersonation_readonly"
  | "stripe_unavailable"
  | "too_many_accounts"
  | "stripe_required"
  | "order_links_required"
  | "links_incomplete"
  | "confirmation_required"
  | "integration_in_progress"
  // F171: the "Napojení plateb" step: not bought, already closed, a checklist
  // with unticked items, or a step the client cannot see yet.
  | "payments_setup_not_included"
  | "payments_setup_completed"
  | "payments_checklist_incomplete"
  | "payments_step_locked"
  // F99: the page states its tariffs, so the manual mode is refused.
  | "tariffs_known"
  // F99: rows may be written only after the manual mode is turned on.
  | "manual_not_enabled"
  // F105: a pushed license key whose bot username does not match the
  // client's own bot - the Mini matched the wrong expert directory.
  | "bot_mismatch"
  // F107: a handover verification the server cannot anchor to the client's
  // instance folder - either none is recorded yet, or the runner verified a
  // different folder (every pre-F107 fact named the Know-how intake box).
  | "instance_folder_unknown"
  | "folder_mismatch"
  // F112: the partner's "Vratit k oprave" on a bot transfer. Each refusal
  // names a DIFFERENT reality: the bot really did change hands inside
  // Telegram (so there is nothing to hand back, and the F104 lock is finally
  // true), or the last transfer is not a failed one at all - queued, running,
  // or already resubmitted by the client.
  | "transfer_completed"
  | "no_failed_transfer"
  // F114: the first podnět used to answer every refusal with one
  // `state_conflict`, so the client read "Podnět se nepodařilo odeslat." over
  // three different realities. Each names its own next move: the Buddy's trial
  // chat is not ready yet (ours), and the tester attribution opens only once
  // the bot has changed hands (the client's own step, already on screen).
  | "trial_not_ready"
  | "tester_before_transfer"
  // D11 (audit F115): the job KEEPS its failed status after a return, so a
  // second click used to pass and mail the client twice about the same thing.
  | "already_returned"
  // F137: the app release lifecycle, named so the console can redraw instead of
  // guessing. `release_transition_invalid` is the platform's own "anything else
  // -> 400"; `store_links_required` names WHICH link a live app is missing.
  | "release_transition_invalid"
  | "store_links_required"
  | "app_not_included"
  // B1 (audit F115): rewriting the owner's address. `email_taken` means
  // another user already signs in with it (never overwrite an identity),
  // `email_unchanged` that the client already has exactly this address - two
  // different next moves, so two names.
  | "email_taken"
  | "email_unchanged"
  // D13 (audit F115): this address already owns a client. Two records on one
  // e-mail split silently: the team works in one, the login link (oldest
  // membership) sends the client into the other.
  | "owner_has_client"
  // F139: the admin picked a partner from the unassigned queue and that
  // partner was deactivated or demoted while the list sat on screen. Its own
  // name, because the next move is picking somebody else - not reloading and
  // not trying the same click again.
  | "partner_unavailable"
  // O9 (audit F115): a staff note may be deleted only by its AUTHOR, and only
  // while Alex has not been briefed with it.
  | "not_own_note"
  | "note_sent_to_agent"
  // Publish gate (Dow Janes, Pavel 2026-08-19): publishing an edit result while
  // a DIFFERENT version of the kind is live. Its own name, because repeating
  // the click can never work and reloading changes nothing: the repair is a
  // fresh edit of the version the client sees now. When NOTHING is live the
  // gate does not fire at all - a first publish displaces nobody.
  | "edit_base_not_live"
  | "netlify_token_required"
  | "netlify_auth_failed"
  | "netlify_deploy_failed"
  | "netlify_deploy_pending"
  | "netlify_domain_failed"
  | "netlify_site_required"
  | "operation_in_progress"
  | "artifact_unavailable"
  | "pdf_unavailable"
  // F170: podněty -> prompt patch. One open request per client; the selection
  // must be this client's open beta podněty; a machine write must carry the
  // lease it claimed.
  | "already_open"
  | "invalid_feedback"
  | "lease_mismatch"
  | "internal_error";

export interface ApiError {
  ok: false;
  error: ApiErrorCode;
}

export interface MembershipDto {
  clientId: string;
  clientName: string;
  role: "owner" | "collaborator";
}

export interface AuthMeResponse {
  ok: true;
  /**
   * Tabsync: the id of the web session this answer was read from. The
   * identity fingerprint (src/identity-sync.ts) needs it to tell one
   * impersonation of a client apart from a NEW impersonation of the same
   * client. Optional so an older Worker payload still parses.
   */
  sessionId?: string;
  user: {
    id: string;
    email: string;
    displayName: string | null;
    locale: Locale;
    staffRole: "partner" | "admin" | null;
  };
  activeClientId: string | null;
  memberships: MembershipDto[];
  csrfToken: string;
  /**
   * F138e: the floating bar states WHO is looking, at WHOM, in WHICH mode and
   * for how much longer, so every one of those facts travels with the session
   * instead of being guessed on the surface.
   */
  impersonation: null | {
    clientId: string;
    clientName: string;
    /** 'look' = jen koukám, 'act' = the allowed writes are open. */
    mode: "look" | "act";
    /** The staff role of the person behind the preview. */
    staffRole: "partner" | "admin";
    /** Display name of the person behind the preview, when we hold one. */
    staffName: string | null;
    /** The hour runs out here; the bar counts down to it and returns in time. */
    expiresAt: string;
  };
  environment: "local" | "dev" | "prod";
}

export interface HealthResponse {
  ok: true;
  service: "launchpad";
  version: string;
  environment: "local" | "dev" | "prod";
}

export interface RedeemResponse {
  ok: true;
  status: "authenticated";
  activeClientId: string | null;
  returnTo?: string;
}

export type SectionKey =
  | "home"
  | "onboarding_call"
  | "q1"
  | "knowhow"
  | "training"
  | "beta"
  | "marketing_call"
  | "q2"
  | "sales"
  | "stripe"
  | "handover"
  | "app";

export interface SectionDto {
  key: SectionKey;
  state: "locked" | "next" | "working" | "done";
  primaryNext: boolean;
  rowVersion: number;
  changedAt: string;
  unlockConditionKey: string;
}

export type SectionState = SectionDto["state"];

// D21 sales chapter adapter for the rail. Additive by design: U1a can render
// steps 11-19 without inventing state from the legacy sales/stripe/handover
// sections. Semantics match SectionDto: next = the client can act now,
// working = unlocked but the team or an external system is working.
export type SalesStepKey =
  | "strategy"
  | "salespage"
  | "app_questionnaire"
  | "emails"
  | "webinar"
  | "stripe"
  // F171: "Napojení plateb", drawn only for a client with the payment setup.
  | "payments"
  | "handover"
  | "publish"
  | "release";

export interface SalesStepDto {
  key: SalesStepKey;
  state: SectionDto["state"];
}

export interface CallDto {
  id: string;
  kind: "onboarding" | "marketing";
  locale: Locale;
  startsAt: string;
  zoomUrl: string | null;
  recordingUrl: string | null;
  enrollmentStatus: "enrolled" | "attended" | "watched_recording" | null;
}

// Fields are nullable: an assigned partner surfaces even with an incomplete
// staff profile (Pavel: the Stripe step must show the partner's contact; a
// missing photo must not hide the whole partner).
export interface PartnerDto {
  userId: string;
  displayName: string | null;
  photoUrl: string | null;
  calendlyUrl: string | null;
  publicEmail: string | null;
  /** F167: t.me link to the partner (username, else phone); absent = no Telegram button. */
  telegramUrl?: string | null;
  // F146: no `bio`. The card's sentence is a locale key, and the column stays
  // in the database unread rather than travelling to a client who cannot use it.
}

export interface DfyBuildStepDto {
  step: string;
  ok: boolean;
  ts: string;
}

/** Where the numbers came from. Never guessed: an estimate without a source
 * is not shown at all. */
export type DfyEtaSource = "step_history" | "gate_total" | "cross_gate_prior";

export interface DfyEtaDto {
  gateKey: string;
  catalogVersion: string;
  currentStepKey: string | null;
  currentStepNumber: number;
  totalSteps: number;
  completedSteps: number;
  remainingMedianMs: number;
  remainingP25Ms: number;
  remainingP75Ms: number;
  etaAt: string;
  etaFrom: string;
  etaTo: string;
  /** Historical runs that contributed to EVERY number shown, not the sum of
   * per-step counts. */
  sampleSize: number;
  source: DfyEtaSource;
  /** "provisional" never leaves the server in production; it exists so QA can
   * inspect a thin sample without it ever reading as an ordinary estimate. */
  confidence: "standard" | "provisional";
  calculatedAt: string;
}

/** A step of THIS run that the server saw finish, with the time it took.
 * `durationMs` is measured from the previous boundary (run start, or the
 * previous step's observation), never from the runner's own clock. */
export interface DfyStepObservationDto {
  stepKey: string;
  observedAt: string;
  durationMs: number;
}

/** What history knows about one step. Never a promise about the whole build:
 * the total estimate keeps its own N=5 floor (F13). */
export interface DfyStepTypicalDto {
  stepKey: string;
  medianMs: number;
  /** Validated runs behind this one step; never below
   * `DFY_STEP_TYPICAL_MIN_SAMPLE`. */
  sampleSize: number;
}

/**
 * The measured story of a running build: what each finished step cost, what
 * each step usually costs, and how many validated runs this gate has behind
 * it. Available even when `estimateDfyEta` returns nothing, which is the
 * normal cold-start state and exactly when the partner most needs to see
 * something honest.
 */
export interface DfyBuildTimingsDto {
  observed: DfyStepObservationDto[];
  typical: DfyStepTypicalDto[];
  /** Validated historical runs of this gate. 0 = this is the first one. */
  historyRuns: number;
  /** Server-observed moment the CURRENT step began; the run start until the
   * first step lands. Drives "na tomhle kroku uz X min". */
  currentStepSince: string;
}

/**
 * Where a running build stands, independent of any estimate.
 *
 * The queue used to carry only `eta`, and an estimate legitimately does not
 * exist during cold start, so a partner watching a live build saw nothing at
 * all: no step, no count, no time (Pavel 2026-08-05). Position is always
 * knowable and is therefore always sent.
 */
export interface DfyBuildPositionDto {
  runId: string;
  gateKey: string;
  startedAt: string;
  currentStepKey: string | null;
  currentStepNumber: number;
  totalSteps: number;
  completedSteps: number;
  currentStepSince: string;
}

export interface DfyBuildRunDto {
  runId: string;
  gateKey: string;
  /** The ordered step list THIS run followed, when it sent one. Without it a
   * surface would count "krok X z Y" against the current catalog while the
   * server measured the estimate against the manifest, and the two numbers on
   * one screen would disagree. */
  stepManifest?: string[] | null;
  status: "running" | "done" | "failed";
  steps: DfyBuildStepDto[];
  startedAt: string;
  updatedAt: string;
  finishedAt: string | null;
  note: string | null;
  /** Absent whenever the history cannot carry an honest number. */
  eta?: DfyEtaDto;
  /** Measured step times. Present for a live run, absent for a finished one. */
  timings?: DfyBuildTimingsDto;
}

export interface HomeResponse {
  ok: true;
  client: {
    id: string;
    name: string;
    /** F64: drives the {{g:}} markers in Czech copy; null renders "spustil/a". */
    gender: ClientGender;
    marketLanguage: Locale;
    includesApp: boolean;
    purchaseSource: "direct" | "upsell" | "webinar";
    instanceStartMode: "new" | "existing";
    /**
     * F143 (Pavel 2026-08-13): `sales_only` = the Buddy was finished outside
     * the system, so the client's journey is chapter 2 without the handover.
     * REQUIRED, never optional: every surface that draws the journey has to
     * read it, and an optional field is how one of them silently keeps
     * drawing seventeen steps for a client who has six.
     */
    journeyMode: JourneyMode;
    membershipRole: "owner" | "collaborator";
  };
  sections: SectionDto[];
  primaryNext: SectionKey | null;
  nextCall: CallDto | null;
  recordingFallbackUrl: string | null;
  calls: { onboarding: CallDto | null; marketing: CallDto | null };
  callRecordings: { onboarding: string | null; marketing: string | null };
  profilePhotoUrl: string | null;
  partner: PartnerDto | null;
  profile:
    | { status: "building"; facts: []; lastUpdatedAt: string }
    | { status: "ready"; facts: Array<{ id: string; category: FactCategory; effectiveText: string }>; lastUpdatedAt: string };
  /**
   * F145: what the profile step may honestly say about the initial research.
   *
   * Replaces the `profileResearchPending` / `profileResearchFailed` pair,
   * which could not tell a job a runner is WORKING ON from one merely sitting
   * in a queue nobody drains - so "první podklady právě vznikají" stood over a
   * machine that had stopped, or had never started. Derived once by
   * `profileResearchStateFrom` (shared/profile-research.ts); no surface may
   * recombine it from parts.
   */
  profileResearch?: ProfileResearchState;
  invites: { canManage: boolean; pendingCount: number };
  salesReviewCount: number;
  /** Durable owner confirmation after reviewing the generated profile. */
  profileConfirmed?: boolean;
  /** Durable owner confirmation that the Apple Developer account exists. */
  appleAccountConfirmed?: boolean;
  /** Feedback opens only after the verified Telegram bot transfer. */
  feedbackUnlocked?: boolean;
  /**
   * F134b: the instance owner role is recorded, so the instance is the
   * client's and we cannot configure it any more. The closed feedback step
   * says THAT instead of "we applied what you learned during testing".
   */
  ownershipTransferred?: boolean;
  /** Ordered D21 steps 11-19; never derive these gates in the client. */
  /** Ordered D21 sales-chapter truth. Optional: pre-U1c fixtures and the
      rail's legacy fallback must keep typechecking without it. */
  salesSteps?: SalesStepDto[];
  /**
   * 🔴 F152-W4 (Pavel 2026-08-14): all four client materials approved, from
   * the SAME derivation that opens the handover on the full journey
   * (`buildSalesJourney`). On `sales_only` this is the end of the journey -
   * "schválený webinář = konec cesty" - and it cannot be recomputed from
   * `salesSteps`, because an approved-but-unpublished sales page and one still
   * in review are both `"next"` there (F120's three phases).
   *
   * Optional for the same reason `salesSteps` is: fixtures and cached payloads
   * from before this wave must keep typechecking, and `undefined` reads as NOT
   * finished, never as finished.
   */
  salesMaterialsApproved?: boolean;
  appInterest: boolean;
  /**
   * F123: how far the client is INSIDE the rail steps that contain steps.
   * Derived once on the server (worker/src/rail-substeps.ts); optional so
   * cached and fixture payloads keep typechecking, and an absent entry simply
   * draws no arc.
   */
  stepProgress?: import("./rail-substeps").RailStepProgressMap;
  videos: { onboarding: HomeVideoDto | null; marketing: HomeVideoDto | null };
  /** Mobile app wizard summary (null = untouched or package without app). */
  appWizard: import("./app-wizard").AppWizardSummary | null;
}

// M6 (meeting D6): gated videos carried by the call sections. Home only says
// whether a video exists and how far the client is; the player fetches the
// url and fresh resume position through GET /api/video/:videoKey.
export type VideoKey = "onboarding_video" | "marketing_video";

export interface HomeVideoDto {
  key: VideoKey;
  title: string | null;
  completed: boolean;
  watchedPct: number;
}

export interface VideoDetailDto {
  key: VideoKey;
  url: string;
  title: string | null;
  positionSeconds: number;
  maxWatchedSeconds: number;
  durationSeconds: number | null;
  completed: boolean;
  completionFraction: number;
  /** F81: the effective unlock moment in seconds (configured unlock_at, else
      95% of the known duration; null until a duration is known). */
  unlockAtSeconds: number | null;
  /** The next step is open for this viewer (threshold reached or completed). */
  unlockReady: boolean;
}

export interface VideoResponse {
  ok: true;
  video: VideoDetailDto;
}

export interface VideoProgressResponse {
  ok: true;
  positionSeconds: number;
  maxWatchedSeconds: number;
  durationSeconds: number | null;
  completed: boolean;
  unlockAtSeconds: number | null;
  unlockReady: boolean;
  unlocked: Partial<Record<string, { state: string; primaryNext: boolean; rowVersion: number }>> | null;

}

export type FactCategory = "identity" | "business" | "audience" | "offer" | "voice" | "proof" | "other";
export type ProfileFactAction = "confirm" | "correct" | "irrelevant";

export interface ProfileHistoryDto {
  id: string;
  factId: string;
  action: ProfileFactAction;
  text: string | null;
  effectiveText: string | null;
  createdAt: string;
  author: { id: string; displayName: string | null; email: string };
  attachment: null | {
    id: string;
    url: string;
    transcriptStatus: "pending" | "processing" | "done" | "error";
  };
}

export interface ProfileFactDto {
  id: string;
  factKey: string;
  category: FactCategory;
  originalText: string;
  effectiveText: string;
  source: unknown;
  confidence: number | null;
  included: boolean;
  action: ProfileFactAction | null;
  updatedAt: string;
  history: ProfileHistoryDto[];
}

export interface ProfileResponse {
  ok: true;
  dossierVersion: number;
  updateCutoff: string;
  latestUpdateAt: string | null;
  facts: ProfileFactDto[];
}

export type ResourceKey = "drive_folder" | "url_doc" | "trial_link" | "license" | "license_key" | "bot_transfer_state" | "app_state" | "stripe_setup" | "q1_question_overrides" | "q2_question_overrides" | "knowhow_google_account" | "handover_telegram" | "app_interest" | "instance_team_request" | "sales_edit_request" | "onboarding_video" | "marketing_video" | "partner_materials" | "step_confirmations" | "knowhow_handover" | "instance_folder" | "staff_overrides";

export interface ResourceDto {
  key: ResourceKey;
  value: Record<string, unknown>;
  updatedAt: string;
}

export interface AutomationStatsDto {
  medianMs: number | null;
  sampleSize: number;
}

// F110: what the client is told while a Předání fact is still on its way.
// `requested` = the signal is out and the Mini is on it; `delayed` = it has
// been out longer than it should be, so the copy says so instead of promising
// "any moment now" forever. Null = that fact is not pending.
export type HandoverFactWait = "requested" | "delayed";

export interface HandoverAutomationDto {
  folder: HandoverFactWait | null;
  licenseKey: HandoverFactWait | null;
}

/**
 * F135b: what the app questionnaire step may claim about a MACHINE prefill.
 *
 * `prefilled` = the automation really wrote answers into this client's wizard.
 * `pending` = the lane is demonstrably running and this client is in its
 * queue. `delayed` = the same, but it has taken longer than it should.
 * ABSENT = no prefill is coming, so the step promises none - which is the
 * honest answer while the D20 app lane is not armed on the Mac Mini.
 */
export type AppPrefillState = "prefilled" | "pending" | "delayed";

export interface ResourcesResponse {
  ok: true;
  resources: ResourceDto[];
  automationStats?: {
    knowhow_provisioning: AutomationStatsDto;
  };
  /** Absent once both facts are in place. */
  handoverAutomation?: HandoverAutomationDto;
  /** Absent = nothing is prefilling this client's app wizard. */
  appPrefill?: AppPrefillState;
}

// price_proposal retired 2026-07-24 (D13): pricing lives in the plan
// (Prodejní strategie). Legacy rows are filtered out of client reads.
export type DeliverableKind = "audit" | "plan" | "salespage" | "emails" | "webinar" | "avatar";
export type DeliverableStatus = "draft" | "review" | "approved";

export interface DeliverableDto {
  id: string;
  kind: DeliverableKind;
  version: number;
  title: string;
  url: string;
  status: DeliverableStatus;
  published: boolean;
  publishedAt: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  // F104: true when approved_by is a staff user - the client card then says
  // the approval was confirmed by the partner on a call.
  approvedByStaff?: boolean;
  createdAt: string;
  updatedAt: string;
  contentMd: string | null;
  salespageManifest?: Record<string, unknown> | null;
}

// The generated AI avatar is a sales material, but it lives in attachments
// (scope ai_avatar, machine-written), not in deliverables. The sales grid
// gets its honest state here: ready (image exists), preparing (client photo
// exists, generation pending), none (no photo - no card).
export interface AvatarMaterialDto {
  state: "none" | "preparing" | "ready";
  url?: string;
  updatedAt?: string;
  automationStats?: AutomationStatsDto;
}

// One order URL per tariff of the generated sales page (F82). The tariffs
// themselves are a FACT of the approved page, never the client's writing
// (F99): `source` says which of the two server sources named them, and the
// client only adds the URL. `custom` (= source "manual") is the emergency for
// a page neither source can read.
export interface OrderLinkTariffDto {
  id: string;
  name: string;
  url: string | null;
}

export interface OrderLinksDto {
  pageApproved: boolean;
  custom: boolean;
  /** F99: "manifest" / "page" = the server named the tariffs and the client
   *  adds links only; "none" = nothing could be read, so the manual mode is
   *  offered; "manual" = the client turned that mode on and writes the rows. */
  source: "manifest" | "page" | "none" | "manual";
  /** F99: the manual mode may be offered only while the server knows no
   *  tariffs. A known set is never editable. */
  manualAvailable: boolean;
  tariffs: OrderLinkTariffDto[];
  confirmed: boolean;
  integration: { status: "sent" | "working"; requestedAt: string } | null;
  integratedAt: string | null;
  /** Historic client who reached the old stripe_receiving gate: the
   * take-away stays unlocked, nothing locks back (F82). */
  legacyStripeUnlocked: boolean;
}

// Sales page delivery state (meeting 24.7. D10 + D11; gate remapped by F82):
// the lock mirrors the server-enforced gate (milestone order_links_integrated,
// or the historic stripe_receiving), netlify carries the client's own
// published site - never the token, at most its last 4 chars.
export interface SalespageDeliveryDto {
  locked: boolean;
  netlify: {
    siteUrl: string;
    dnsTarget: string;
    publishedVersion: number;
    publishedAt: string;
    tokenHint: string;
  } | null;
  /** F82 order-links block state; absent on cached pre-F82 payloads. */
  orderLinks?: OrderLinksDto | null;
  /**
   * F137: the publication FACT - which act finished phase 3 and when. Written
   * by the first of the two take-away acts (download or Netlify publish) and
   * read by the phase card and the rail's legacy fallback. Optional, so cached
   * and fixture payloads keep typechecking; absent simply means not yet.
   *
   * 🔴 It is not a lock. Both actions and the preview stay usable afterwards -
   * the client may come back and do the other one (F124 idiom).
   */
  published?: { at: string; via: "download" | "netlify" } | null;
}

export interface DeliverablesResponse {
  ok: true;
  deliverables: DeliverableDto[];
  avatar?: AvatarMaterialDto;
  salespage?: SalespageDeliveryDto;
  /** Ordered server truth of the D21 sales chapter (same as HomeResponse.salesSteps).
      Optional so pre-U1c fixtures and cached clients keep typechecking; the
      rail falls back to its legacy D21 derivation when absent. */
  salesSteps?: SalesStepDto[];
}

export type MilestoneKey =
  | "telegram_launch"
  | "bot_transferred"
  | "stripe_receiving"
  | "assets_handed"
  | "first_campaign_or_trial"
  | "app_live"
  | "order_links_integrated";

export interface MilestoneDto {
  key: MilestoneKey;
  reachedAt: string;
  setBy: string;
  evidence: Record<string, unknown>;
}

export interface MilestonesResponse {
  ok: true;
  milestones: MilestoneDto[];
}

export type FeedbackStatus = "new" | "processed";

export interface FeedbackDto {
  id: string;
  client: { id: string; name: string };
  text: string | null;
  status: FeedbackStatus;
  // Tester attribution: the client's own observation, or one relayed from a
  // tester (optionally named) - structured signal for the partner inbox.
  source: "self" | "tester";
  testerName: string | null;
  createdAt: string;
  updatedAt: string;
  author: { id: string; displayName: string | null; email: string };
  screenshot: null | { id: string; url: string };
  audio: null | {
    id: string;
    url: string;
    transcriptStatus: "pending" | "processing" | "done" | "error";
  };
}

export interface FeedbackListResponse {
  ok: true;
  feedback: FeedbackDto[];
}

export interface StaffMember {
  id: string;
  email: string;
  displayName: string | null;
  role: "partner" | "admin";
  isActive: boolean;
  clientCount: number;
  lastSeenAt: string | null;
  profileComplete: boolean;
  createdAt: string;
  self: boolean;
}

export interface StaffRosterResponse {
  ok: true;
  members: StaffMember[];
  summary: { adminCount: number; partnerCount: number };
}

export interface AdminActivityEntry {
  id: string;
  type: string;
  createdAt: string;
  clientId: string | null;
  clientName: string | null;
  /** Canonical test-tenant verdict computed server-side from name AND email;
   * the feed used to re-derive it from the name alone and let seeded clients
   * through the global filter (audit a3-06). */
  isTest?: boolean;
  actorName: string | null;
  summary: Record<string, string>;
}

export interface AdminActivityResponse {
  ok: true;
  entries: AdminActivityEntry[];
}

/** Servis wave 5: the filtered activity timeline, the recovery list and the
 * default-content overview. The tool and outcome verdicts are computed on the
 * server (worker/src/routes/admin-diag.ts) so the filters and the rendered
 * rows cannot disagree. */
export interface AdminActivityLogEntry extends AdminActivityEntry {
  tool: string;
  outcome: "error" | "intervention" | "progress";
}

export interface AdminActivityLogResponse {
  ok: true;
  entries: AdminActivityLogEntry[];
  hasMore: boolean;
  cursor: { at: string; id: string } | null;
  counts: Record<string, number>;
  total: number;
}

export interface AdminDiagnosticsTranscript {
  id: string;
  clientId: string;
  clientName: string;
  isTest: boolean;
  scope: string;
  fileName: string | null;
  state: "error" | "stalled";
  lastError: string | null;
  attemptCount: number;
  updatedAt: string;
  retryable: boolean;
}

export interface AdminDiagnosticsResponse {
  ok: true;
  transcripts: AdminDiagnosticsTranscript[];
}

export interface AdminDossierVersion {
  version: number;
  status: string;
  source: string;
  expectedFactCount: number;
  receivedFactCount: number;
  importedAt: string;
  finalizedAt: string | null;
  failureReason: string | null;
}

export interface AdminDossierStateResponse {
  ok: true;
  client: { id: string; name: string };
  versions: AdminDossierVersion[];
}

export interface AdminContentVideoUsage {
  clients: number;
  ownVideo: number;
  usingDefault: number;
  completed: number;
}

/**
 * F116: one market's recording of one step. The default video used to be a
 * single row for everybody, so an American client was gated behind a Czech
 * recording; Servis holds a slot per market now and each slot counts only the
 * clients that market can reach.
 */
export interface AdminContentVideoSlot {
  locale: Locale;
  configured: {
    url: string;
    title: string;
    durationSeconds: number | null;
    unlockAtSeconds: number | null;
    updatedAt: string | null;
    /**
     * Still served from the pre-F116 shared row, which only Czech falls back
     * to. Saying so keeps the panel from reporting a per-market slot the
     * admin never filled in as one they did.
     */
    legacy: boolean;
  } | null;
  usage: AdminContentVideoUsage;
  usageWithTest: AdminContentVideoUsage;
}

export interface AdminContentVideo {
  key: VideoKey;
  slots: AdminContentVideoSlot[];
}

export interface AdminContentSettingsResponse {
  ok: true;
  videos: AdminContentVideo[];
}
